import type { HTMLAttributes, Ref } from 'react'
import { cx } from '@/shared/lib'
import s from './Skeleton.module.css'

/**
 * Форма заглушки. Скелетон повторяет геометрию будущего контента, чтобы при подстановке
 * данных экран не прыгал.
 */
export type SkeletonShape = 'line' | 'pill' | 'circle' | 'photo' | 'block'

/** Ступень высоты. Смысл зависит от формы: кегль текста, высота контрола, диаметр аватара. */
export type SkeletonSize = 'xs' | 'sm' | 'md' | 'lg'

/** Доля ширины родителя: кит рисует строки разной длины, чтобы заглушка читалась как текст. */
export type SkeletonWidth = 'full' | 'long' | 'medium' | 'short'

export type SkeletonProps = {
  shape?: SkeletonShape
  size?: SkeletonSize
  /** Работает у `line` и `pill`; у `circle`, `photo` и `block` ширину задаёт форма. */
  width?: SkeletonWidth
  /** Занять остаток flex-строки: заглушка кнопки рядом с круглой кнопкой-иконкой. */
  grow?: boolean
  /**
   * Живое объявление загрузки.
   * Задано — корень становится `role="status"` со скрытой подписью; так помечается
   * одиночная заглушка целой области (график, превью документа).
   * Не задано — плитка декоративна (`aria-hidden`): о загрузке говорит область,
   * у которой стоит `aria-busy` и живёт одна живая строка на всех.
   */
  label?: string
  ref?: Ref<HTMLSpanElement>
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const SHAPE_CLASS: Record<SkeletonShape, string | undefined> = {
  line: s.line,
  pill: s.pill,
  circle: s.circle,
  photo: s.photo,
  block: s.block,
}

const SIZE_CLASS: Record<SkeletonSize, string | undefined> = {
  xs: s.sizeXs,
  sm: s.sizeSm,
  md: s.sizeMd,
  lg: s.sizeLg,
}

const WIDTH_CLASS: Record<SkeletonWidth, string | undefined> = {
  full: s.widthFull,
  long: s.widthLong,
  medium: s.widthMedium,
  short: s.widthShort,
}

/** Высоту берут из ступени: у photo её задаёт пропорция 4/3, у block — родитель. */
const SHAPES_WITH_SIZE: SkeletonShape[] = ['line', 'pill', 'circle']

/** Ширина настраивается: круг равен своей высоте, плитка и блок тянутся по родителю. */
const SHAPES_WITH_WIDTH: SkeletonShape[] = ['line', 'pill']

/**
 * Заглушка формы контента на время первой загрузки.
 * Операции короче секунды и дозагрузка страницы — это Spinner, а не скелетон.
 * Контракт: docs/ui-kit/contracts/skeleton.md
 */
export function Skeleton({
  shape = 'line',
  size = 'md',
  width = 'full',
  grow = false,
  label,
  className,
  ref,
  ...rest
}: SkeletonProps) {
  const announced = Boolean(label)

  return (
    <span
      {...rest}
      ref={ref}
      className={cx(
        s.root,
        SHAPE_CLASS[shape],
        SHAPES_WITH_SIZE.includes(shape) && SIZE_CLASS[size],
        SHAPES_WITH_WIDTH.includes(shape) && WIDTH_CLASS[width],
        grow && s.grow,
        className,
      )}
      data-shape={shape}
      role={announced ? 'status' : undefined}
      aria-hidden={announced ? undefined : true}
    >
      {announced ? <span className={s.label}>{label}</span> : null}
    </span>
  )
}
